import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { XRole } from '../../config/module-types';
import {
  getModule,
  DEFAULT_MODULE_ID,
  defaultModuleForRole,
} from '../../config/module-config';

export interface SessionUser {
  name: string;
  email: string;
  role: XRole;
  /** The user's primary organization (used to gate on subscription status). */
  organizationId?: string;
}

export interface Organization {
  id: string;
  name: string;
  ownerName: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface LayoutState {
  isAuthenticated: boolean;
  /**
   * Whether the user has entered (picked) an organization this session. Org
   * users land on the organization picker after login until they choose one;
   * platform owner / broker bypass it.
   */
  orgSelected: boolean;
  activeModuleId: string;
  /** Current in-app route path (we navigate via Redux instead of a router dep). */
  currentPath: string;
  /**
   * Bumped on every navigate/selectModule dispatch, including re-clicking the
   * already-active nav item. Layout keys the rendered screen off this so a
   * repeat click remounts it — otherwise a screen with its own internal "sub
   * view" state (e.g. an inventory entity's overview mode) never resets when
   * you click its sidebar item again, since the screen component instance
   * doesn't change.
   */
  navSeq: number;
  /**
   * A drilled-into record's display title (e.g. a product name), shown as a
   * third breadcrumb segment after the page title — set by EntityOverview
   * while a "view" screen is open, cleared when it unmounts.
   */
  breadcrumbDetail: string | null;
  sidebarOpen: boolean;
  user: SessionUser;
  organization: Organization;
}

// Platform owner and broker are not org members — they skip the org picker.
const bypassesOrgPicker = (role: XRole): boolean =>
  role === 'platform owner' || role === 'broker';

/** First menu path of a module (Home -> "/"), used when switching modules. */
const defaultPathFor = (moduleId: string): string => {
  const module = getModule(moduleId);
  return module?.menu?.[0]?.path ?? '/';
};

// The access token survives a page refresh (authSlice reads it straight back
// out of localStorage), but this slice's session — who's signed in, which
// role/org — used to live only in memory, so a refresh always bounced an
// otherwise-still-logged-in user back to the Login screen. Persisting the
// session identity here (mirroring authSlice's token persistence) lets boot
// restore it instead.
const SESSION_STORAGE_KEY = 'layoutSessionUser';

interface PersistedSession {
  user: SessionUser;
  orgSelected: boolean;
  organization?: Partial<Organization>;
}

const persistSession = (
  user: SessionUser,
  orgSelected: boolean,
  organization?: Partial<Organization>,
) => {
  const payload: PersistedSession = { user, orgSelected, organization };
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
};

const clearPersistedSession = () => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
};

// Only trust the persisted session while the access token that goes with it
// is still present — if tokens were cleared (logout, failed silent refresh
// elsewhere) but this blob wasn't, we must not resurrect a stale session.
const loadPersistedSession = (): PersistedSession | null => {
  if (!localStorage.getItem('accessToken')) return null;
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    return parsed?.user?.role ? parsed : null;
  } catch {
    return null;
  }
};

const defaultOrganization: Organization = {
  id: 'org-1',
  name: 'Test Organization',
  ownerName: 'John Doe',
  createdBy: 'Jane Smith',
  createdAt: '2023-01-01T00:00:00Z',
  updatedAt: '2023-01-01T00:00:00Z',
};

const buildInitialState = (): LayoutState => {
  const persisted = loadPersistedSession();
  if (!persisted) {
    return {
      isAuthenticated: false,
      orgSelected: false,
      activeModuleId: DEFAULT_MODULE_ID,
      currentPath: defaultPathFor(DEFAULT_MODULE_ID),
      navSeq: 0,
      breadcrumbDetail: null,
      sidebarOpen: true,
      user: { name: 'Bapi Mallick', email: 'bapi@multiplied.ai', role: 'platform owner' },
      organization: defaultOrganization,
    };
  }

  const moduleId = defaultModuleForRole(persisted.user.role);
  return {
    isAuthenticated: true,
    orgSelected: persisted.orgSelected,
    activeModuleId: moduleId,
    currentPath: defaultPathFor(moduleId),
    navSeq: 0,
    breadcrumbDetail: null,
    sidebarOpen: true,
    user: persisted.user,
    organization: { ...defaultOrganization, ...persisted.organization },
  };
};

const initialState: LayoutState = buildInitialState();

const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    login(state) {
      state.isAuthenticated = true;
    },
    loginAs(
      state,
      action: PayloadAction<SessionUser & { orgSelected?: boolean }>,
    ) {
      const { orgSelected, ...user } = action.payload;
      state.user = user;
      state.isAuthenticated = true;
      // Org users see the organization picker first; platform owner / broker go
      // straight in. `orgSelected` can be forced (e.g. signup → its one org).
      state.orgSelected = orgSelected ?? bypassesOrgPicker(user.role);
      // Land each role on its own default module.
      const moduleId = defaultModuleForRole(user.role);
      state.activeModuleId = moduleId;
      state.currentPath = defaultPathFor(moduleId);
      persistSession(state.user, state.orgSelected, state.organization);
    },
    // Enter a chosen organization (from the picker or after signup): adopt its
    // role, set it active, and reveal the app shell.
    enterOrganization(
      state,
      action: PayloadAction<{
        organizationId: string;
        organizationName: string;
        role: XRole;
      }>,
    ) {
      const { organizationId, organizationName, role } = action.payload;
      state.user.organizationId = organizationId;
      state.user.role = role;
      state.organization = {
        ...state.organization,
        id: organizationId,
        name: organizationName,
      };
      state.orgSelected = true;
      const moduleId = defaultModuleForRole(role);
      state.activeModuleId = moduleId;
      state.currentPath = defaultPathFor(moduleId);
      persistSession(state.user, state.orgSelected, state.organization);
    },
    // Return to the organization picker without logging out (e.g. from the
    // pending-approval screen to choose a different organization).
    showOrganizationPicker(state) {
      state.orgSelected = false;
      persistSession(state.user, state.orgSelected, state.organization);
    },
    logout(state) {
      state.isAuthenticated = false;
      state.orgSelected = false;
      state.activeModuleId = DEFAULT_MODULE_ID;
      state.currentPath = defaultPathFor(DEFAULT_MODULE_ID);
      clearPersistedSession();
    },
    selectModule(state, action: PayloadAction<string>) {
      state.activeModuleId = action.payload;
      state.currentPath = defaultPathFor(action.payload);
      state.navSeq += 1;
    },
    navigate(state, action: PayloadAction<string>) {
      // Strip any query string (menu paths can carry one, e.g. /forms?module=…).
      state.currentPath = action.payload.split('?')[0];
      state.navSeq += 1;
    },
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebar(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
    },
    setBreadcrumbDetail(state, action: PayloadAction<string | null>) {
      state.breadcrumbDetail = action.payload;
    },
  },
});

export const {
  login,
  loginAs,
  enterOrganization,
  showOrganizationPicker,
  logout,
  selectModule,
  navigate,
  toggleSidebar,
  setSidebar,
  setBreadcrumbDetail,
} = layoutSlice.actions;

export default layoutSlice.reducer;
